import { Component, ErrorInfo, ReactNode } from 'react';
import i18n from '../i18n/index';
import { notifyBugsnag } from '../lib/bugsnag';

const prefersDark = () => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    // The whole app has just been replaced by the fallback screen, so this is
    // the most severe class of client error there is — and until now it went
    // no further than a console the user was never going to read.
    notifyBugsnag(error, { componentStack: errorInfo.componentStack });
  }

  render() {
    if (this.state.hasError) {
      const dark = prefersDark();
      const t = i18n.t.bind(i18n);
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100dvh',
          backgroundColor: dark ? '#0b1120' : '#f9fafb',
          padding: '20px',
        }}>
          <div style={{
            backgroundColor: dark ? '#111827' : 'white',
            padding: '30px',
            borderRadius: '8px',
            boxShadow: dark ? '0 4px 6px -1px rgb(0 0 0 / 0.4)' : '0 4px 6px -1px rgb(0 0 0 / 0.1)',
            maxWidth: '600px',
            width: '100%',
            border: dark ? '1px solid rgba(148, 163, 184, 0.15)' : 'none',
          }}>
            <h1 style={{ color: '#dc2626', marginTop: 0 }}>{t('errors.somethingWentWrong')}</h1>
            <p style={{ color: dark ? '#94a3b8' : '#666' }}>
              {t('errors.renderErrorDescription')}
            </p>
            {this.state.error && (
              <details style={{
                backgroundColor: dark ? '#1f2937' : '#f3f4f6',
                padding: '10px',
                borderRadius: '4px',
                marginTop: '10px',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                fontSize: '12px',
                color: dark ? '#e2e8f0' : 'inherit',
              }}>
                <summary style={{ cursor: 'pointer', color: dark ? '#94a3b8' : '#374151', fontWeight: 'bold' }}>
                  {t('errors.errorDetails')}
                </summary>
                {this.state.error.message}
              </details>
            )}
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: '20px',
                padding: '8px 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              {t('errors.reloadPage')}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
